import { t } from "@hakili/i18n";
import { type BankListItem, bankListAnswerSchema } from "@hakili/contracts";
import { GuardedButton } from "@hakili/ui";
import { Mail, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";

import type { Partner } from "./dealerships.js";
import { accountLine, partnerStatus, DASH, fill, ManagerBadge, StatusChip } from "./partner-cards.js";
import { PartnerAddModal, PartnerTabHeader } from "./partner-tabs.js";

/**
 * `round CN-B`, `CN-B.2`: **the Banques tab, WF-CON-35, to the drawing
 * (`hakili-console.jsx`, `PartenairesScreen`, `onglet === "banques"`).**
 *
 * A card per bank organisation identity holds, joined to `lending`'s bank
 * row where lending holds one: the status chip, the account line DERIVED from
 * the accounts, the volumes from lending's submissions (submitted, returns,
 * decided) , « Financés » is a disbursement `lending` does not answer yet, a
 * dash , the configuration state in lending's own word, the manager named
 * under the administration, « Paramétrer » to the fiche.
 *
 * « + Ajouter une banque » opens the modal over the veil on the cabinet's
 * pattern: the organisation by `POST /organisations`, then what lending's
 * door takes (`POST /banks`), then the first responsible invited by
 * `POST /organisations/{id}/first-admin` , the drawing's third field. The
 * drawing's « Contact direction du crédit » is a fact no door stores yet: it
 * is said in words on the modal, not typed into a field nobody reads.
 */

export interface BankView {
  readonly id: string;
  readonly organisationId: string;
  readonly name: string;
  readonly configState: string;
  readonly active: boolean;
  /**
   * `PANEL-SCOPE`: the door's own verdict for THIS caller on THIS bank (`GET /banks/{id}` only): its type and its portfolio.
   * `false` hides « Admettre / Retirer » , never a dead button; absent (the list's rows), the right alone decides.
   */
  readonly mayAdmitToPanel?: boolean;
  readonly volumes: {
    readonly submitted: number;
    readonly returned: number;
    readonly decided: number;
    readonly offers: number;
    readonly refusals: number;
  };
  /**
   * `L-1`, `M2` (`P1-LND-046`): the bank's convention as `GET /banks/{id}`
   * answers it , `null` while nothing is recorded; `fileFeePct` the founder's
   * field, `null` meaning the platform's default. Absent on the list's rows.
   */
  readonly convention?: {
    readonly signedOn: string | null;
    readonly expiresOn: string | null;
    readonly slaDays: number | null;
    readonly negotiatedNote: string;
    readonly fileFeePct: number | null;
  } | null;
  /**
   * `OD-43`: the four criteria the Hakili filter reads, as `GET /banks/{id}` answers them; each
   * `null` while not configured , and a null criterion excludes nothing. Absent on a caller's stub.
   */
  readonly criteria?: {
    readonly note?: string | null;
    readonly quotiteMaxPct: number | null;
    readonly ltvMaxPct: number | null;
    readonly durationMaxMonths: number | null;
    readonly vehicleAgeMaxYears: number | null;
  };
  /** `round CN-C`, `CN-C.5`, WF-CON-37: the pre-filter in force, riding the read; empty when none. */
  readonly prefilters?: readonly {
    readonly criterion: string;
    readonly thresholdPct: number;
    readonly label: string;
    readonly adjustedAt: string;
  }[];
}

/**
 * `PARTNERS-DONE-STATE`, `M2b`: one parsed row of `GET /api/v1/banks` (`BankListItem`, the contracts package's mirror of the
 * OpenAPI item) as the card reads it. The document declares `volumes` as an object and nothing more, so each count is
 * narrowed here, a missing one as 0 , the same figure the card printed before the parse.
 */
export const bankViewFrom = (item: BankListItem): BankView => {
  const count = (key: string): number => {
    const value = item.volumes[key];
    return typeof value === "number" ? value : 0;
  };
  return {
    id: item.id,
    organisationId: item.organisationId,
    name: item.name,
    configState: item.configState,
    active: item.active,
    volumes: {
      submitted: count("submitted"),
      returned: count("returned"),
      decided: count("decided"),
      offers: count("offers"),
      refusals: count("refusals"),
    },
  };
};

/**
 * `PARTNERS-DONE-STATE`, `M0` (A47): **`GET /api/v1/banks` answered a body that does not match the contract.** Thrown by the
 * boundary ({@link bankViewsFrom}) with the cause in words (the schema's own path and complaint), so the card can say the
 * panel is unknown and the console can log WHY , a refusal by the door (no right) is a different failure and is not this.
 */
export class BankAnswerMismatch extends Error {
  public override readonly cause: string;
  public constructor(cause: string) {
    super(`GET /api/v1/banks: ${cause}`);
    this.name = "BankAnswerMismatch";
    this.cause = cause;
  }
}

/** The boundary: the list's body PARSED with the contracts package's mirror of the OpenAPI item, never cast. */
export const bankViewsFrom = (answer: unknown): readonly BankView[] => {
  const parsed = bankListAnswerSchema.safeParse(answer);
  if (!parsed.success) {
    throw new BankAnswerMismatch(
      parsed.error.issues.map((issue) => `${issue.path.join(".")}: ${issue.message}`).join("; "),
    );
  }
  return parsed.data.banks.map(bankViewFrom);
};

export interface BanksDoors {
  readonly readBanks: () => Promise<readonly BankView[]>;
  /**
   * Round `L-1`, `M0 (d)`: « Inviter le responsable » on the card , identity's
   * first-admin door (`P0-IDN-014`, 06 row 21) for a bank opened without its
   * responsable. Optional so a caller without the door greys the control.
   */
  readonly inviteResponsible?: (input: {
    organisationId: string;
    fullName: string;
    email: string;
    phone: string;
  }) => Promise<void>;
  /**
   * `FX-3`, `M3` (`S2-4`): the two gestures a PENDING invitation owes. The sitting met « Inviter le
   * responsable » greyed with neither beside it, HB Bank and Hbank at « Invitation envoyée » with 0 users,
   * and one of those tokens in a screenshot , a credential seen is burned, and there was no way to burn it.
   *
   * Both answer a sentence or nothing: a refusal is the door's own words (`A47`), never a silence.
   */
  readonly resendInvitation?: (invitationId: string) => Promise<string | null>;
  readonly revokeInvitation?: (invitationId: string) => Promise<string | null>;
  readonly openBank: (input: {
    name: string;
    responsibleName: string;
    responsibleEmail: string;
    identityVerified: boolean;
    conventionReceived: boolean;
  }) => Promise<void>;
}

export interface BanksTabProps extends BanksDoors {
  readonly vue: string;
  readonly partners: readonly Partner[] | null;
  readonly mayOpen: boolean;
  /** Row 21's right, `inviterPremierResponsable`; absent, the invite is greyed with that reason. */
  readonly mayInvite?: boolean;
  readonly onOpened: () => void;
  readonly onOpenSheet: (organisationId: string) => void;
}

const CONFIG_WORDS = new Map<string, Parameters<typeof t>[0]>([
  ["complete", "console.partenaires.configComplete"],
  ["criteresAParametrer", "console.partenaires.configAParametrer"],
]);

export const nameFromEmail = (address: string): string => {
  const local = address.split("@")[0] ?? "";
  return local
    .split(/[._-]+/u)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export function BanksTab(props: BanksTabProps): React.JSX.Element {
  const [banks, setBanks] = useState<readonly BankView[] | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [identityVerified, setIdentityVerified] = useState(false);
  const [agreementReceived, setAgreementReceived] = useState(false);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  // `L-1`, `M0 (d)`: the invitation from the card, the cabinet's pattern.
  const [inviting, setInviting] = useState<Partner | null>(null);
  const [inviteName, setInviteName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteRefusal, setInviteRefusal] = useState<string | null>(null);
  const [inviteWord, setInviteWord] = useState<string | null>(null);
  const [panelUnknown, setPanelUnknown] = useState(false);
  const { readBanks } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const answered = await readBanks();
        if (run.live) {
          setPanelUnknown(false);
          setBanks(answered);
        }
      } catch (failure) {
        // `M0` (A47): a body that breaks the contract is SAID (the panel chip) and LOGGED with its cause; a refusal by
        // the door , the relation view without lending's right still reads its cards from identity , is not.
        if (failure instanceof BankAnswerMismatch) {
          // eslint-disable-next-line no-console -- the one place a contract drift is reported: nobody else would learn of it
          console.error("GET /api/v1/banks does not match the contract:", failure.cause);
        }
        if (run.live) {
          setPanelUnknown(failure instanceof BankAnswerMismatch);
          setBanks([]);
        }
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readBanks, epoch]);

  const organisations = (props.partners ?? []).filter((partner) => partner.type === "banque");
  const responsibleName = nameFromEmail(email.trim());
  const complete =
    name.trim() !== "" &&
    email.includes("@") &&
    responsibleName !== "" &&
    identityVerified &&
    agreementReceived;

  const close = (): void => {
    setOpen(false);
    setSent(null);
    setName("");
    setEmail("");
    setIdentityVerified(false);
    setAgreementReceived(false);
    setRefusal(null);
  };

  return (
    <div data-testid="partenaires-banques" className="flex flex-col gap-4">
      <PartnerTabHeader
        title={t("console.partenaires.banquesTitre")}
        subtitle={
          props.vue === "banques"
            ? fill("console.partenaires.vosBanques", { n: String(organisations.length) })
            : t("console.partenaires.banquesSous")
        }
        control={
          props.mayOpen
            ? {
                label: t("console.partenaires.ajouterBanque"),
                onClick: () => {
                  setOpen(true);
                },
              }
            : { label: t("console.partenaires.ajouterBanque"), reason: t("console.partenaires.ouvrirMotif") }
        }
      />

      {props.partners === null ? (
        <p className="text-xs text-gray-500">{t("common.state.chargement")}</p>
      ) : organisations.length === 0 ? (
        <section className="rounded-2xl border border-gray-200 bg-white px-5 py-6">
          <p className="text-sm leading-relaxed text-gray-500">{t("console.partenaires.banquesAucune")}</p>
        </section>
      ) : (
        <div className="space-y-4">
          {organisations.map((organisation) => {
            const bank = banks?.find((one) => one.organisationId === organisation.id);
            const configKey = bank === undefined ? undefined : CONFIG_WORDS.get(bank.configState);
            const tiles: [string, string][] = [
              [t("console.partenaires.tuileUtilisateurs"), String(organisation.activeCount ?? 0)],
              [
                t("console.partenaires.tuileSoumis"),
                bank === undefined ? DASH : String(bank.volumes.submitted),
              ],
              [
                t("console.partenaires.tuileRetours"),
                bank === undefined ? DASH : String(bank.volumes.returned),
              ],
              [
                t("console.partenaires.tuileDecides"),
                bank === undefined ? DASH : String(bank.volumes.decided),
              ],
              [t("console.partenaires.tuileFinances"), DASH],
              [
                t("console.partenaires.tuileParametrage"),
                bank === undefined
                  ? t("console.partenaires.configSansBanque")
                  : configKey === undefined
                    ? bank.configState
                    : t(configKey),
              ],
            ];
            return (
              <div
                key={organisation.id}
                data-testid={`banque-${organisation.id}`}
                className="rounded-2xl border border-gray-200 bg-white px-6 py-5 hover:border-indigo-200"
              >
                <div className="mb-4 flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-base font-semibold">{organisation.name}</h2>
                      <StatusChip
                        status={partnerStatus(organisation)}
                        activeWord={t("console.partenaires.statutComptesActifs")}
                      />
                      {bank === undefined ? (
                        panelUnknown ? (
                          <span
                            data-testid={`banque-${organisation.id}-panel`}
                            className="whitespace-nowrap rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600"
                          >
                            {t("console.partenaires.panelInconnu")}
                          </span>
                        ) : null
                      ) : (
                        <span
                          data-testid={`banque-${organisation.id}-panel`}
                          className={
                            "whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium " +
                            (bank.active ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-600")
                          }
                        >
                          {bank.active
                            ? t("console.partenaires.panelAu")
                            : t("console.partenaires.panelHors")}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">
                      {organisation.administrator?.email ?? t("console.partenaires.contactAVenir")}
                    </p>
                    <p data-testid="banque-comptes" className="mt-1.5 text-xs text-gray-500">
                      <span className="font-medium text-gray-500">
                        {t("console.partenaires.comptesOrganisation")} ·{" "}
                      </span>
                      {accountLine(organisation)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5">
                    {props.vue === "admin" ? <ManagerBadge name={organisation.managerName} /> : null}
                    {/* `FX-3`, `M3` (`S2-4`): the pending invitation's two gestures, on the card where the
                        sitting looked for them. `invitationId` is `null` once the link is accepted or
                        retired, so the pair appears exactly while something is live to act on. */}
                    {organisation.administrator?.invitationId === null ||
                    organisation.administrator?.invitationId === undefined ? null : (
                      <InvitationControls
                        invitationId={organisation.administrator.invitationId}
                        organisationId={organisation.id}
                        {...(props.resendInvitation === undefined ? {} : { resend: props.resendInvitation })}
                        {...(props.revokeInvitation === undefined ? {} : { revoke: props.revokeInvitation })}
                        onDone={props.onOpened}
                      />
                    )}
                    {/* `PARTNERS-DONE-STATE` (D-1019-3): the responsable's slot is a done state once one is named; the
                        contract's check (h) reads every `data-row-state="done"` for a dead action or an amber sentence. */}
                    <span
                      data-testid={`banque-${organisation.id}-responsable`}
                      data-row-state={
                        organisation.administrator !== null && organisation.administrator !== undefined
                          ? "done"
                          : "pending"
                      }
                      className="contents"
                    >
                      {/* Round `L-1`, `M0 (d)`: the responsable invited from the card, by row 21's right. */}
                      {organisation.administrator !== null && organisation.administrator !== undefined ? (
                        <span
                          data-testid={`banque-${organisation.id}-responsable-nomme`}
                          className="text-sm text-gray-600"
                        >
                          {fill("console.partenaires.responsableNomme", {
                            nom: organisation.administrator.fullName,
                          })}
                        </span>
                      ) : props.mayInvite === true && props.inviteResponsible !== undefined ? (
                        <button
                          type="button"
                          data-testid={`banque-${organisation.id}-inviter`}
                          onClick={() => {
                            setInviting(organisation);
                            setInviteRefusal(null);
                            setInviteWord(null);
                          }}
                          className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                        >
                          {t("console.partenaires.inviterResponsable")}
                        </button>
                      ) : (
                        <GuardedButton
                          disabled
                          reason={t("console.partenaires.inviterDroitMotif")}
                          variant="secondary"
                          testId={`banque-${organisation.id}-inviter`}
                        >
                          {t("console.partenaires.inviterResponsable")}
                        </GuardedButton>
                      )}
                    </span>
                    <button
                      type="button"
                      data-testid="banque-parametrer"
                      onClick={() => {
                        props.onOpenSheet(organisation.id);
                      }}
                      className="rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                    >
                      {t("console.partenaires.parametrer")}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-6 gap-4">
                  {tiles.map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-gray-100 bg-gray-50 px-4 py-3">
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="mt-0.5 text-sm font-medium">{value}</p>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {inviteWord === null ? null : (
        <p className="text-xs text-emerald-700" data-testid="banque-inviter-mot">
          {inviteWord}
        </p>
      )}

      <div className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white px-5 py-4">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" aria-hidden="true" />
        <p className="text-sm leading-relaxed text-gray-600">{t("console.partenaires.banquesNote")}</p>
      </div>

      <PartnerAddModal
        open={inviting !== null}
        title={t("console.partenaires.inviterTitre")}
        onClose={() => {
          setInviting(null);
        }}
      >
        <form
          className="grid grid-cols-2 gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            const organisationId = inviting?.id;
            const door = props.inviteResponsible;
            if (organisationId === undefined || door === undefined || inviteBusy) return;
            setInviteBusy(true);
            setInviteRefusal(null);
            void (async () => {
              try {
                await door({ organisationId, fullName: inviteName, email: inviteEmail, phone: invitePhone });
                setInviteWord(fill("console.partenaires.inviterParti", { email: inviteEmail }));
                setInviting(null);
                setInviteName("");
                setInviteEmail("");
                setInvitePhone("");
                props.onOpened();
              } catch (failure) {
                setInviteRefusal(
                  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                );
              } finally {
                setInviteBusy(false);
              }
            })();
          }}
        >
          <p className="col-span-2 text-xs text-gray-500">
            {inviting === null ? "" : fill("console.partenaires.inviterPour", { bank: inviting.name })}
          </p>
          <label className="text-xs text-gray-600">
            {t("console.cabinets.inviterNom")}
            <input
              value={inviteName}
              onChange={(event) => {
                setInviteName(event.target.value);
              }}
              data-testid="banque-inviter-nom"
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-gray-600">
            {t("console.cabinets.inviterEmail")}
            <input
              value={inviteEmail}
              onChange={(event) => {
                setInviteEmail(event.target.value);
              }}
              data-testid="banque-inviter-email"
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-xs text-gray-600">
            {t("console.cabinets.inviterTelephone")}
            <input
              value={invitePhone}
              onChange={(event) => {
                setInvitePhone(event.target.value);
              }}
              data-testid="banque-inviter-telephone"
              className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            />
          </label>
          {inviteRefusal === null ? null : (
            <p role="alert" className="col-span-2 text-xs text-rose-600" data-testid="banque-inviter-refus">
              {inviteRefusal}
            </p>
          )}
          <button
            type="submit"
            disabled={inviteBusy || inviteName.trim() === "" || inviteEmail.trim() === ""}
            data-testid="banque-inviter-envoyer"
            className="col-span-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
          >
            {t("console.partenaires.inviterResponsable")}
          </button>
        </form>
      </PartnerAddModal>

      <PartnerAddModal open={open} title={t("console.partenaires.ajouterBanque")} onClose={close}>
        {sent !== null ? (
          <div data-testid="banque-invitation-envoyee" className="py-4 text-center">
            <p className="text-base font-semibold">{t("console.partenaires.invitationEnvoyee")}</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-500">
              {fill("console.partenaires.banqueAjoutee", { nom: sent })}
              {props.vue === "banques" ? ` ${t("console.partenaires.ajouteeAuPortefeuille")}` : ""}
            </p>
            <button
              type="button"
              onClick={close}
              className="mt-5 w-full rounded-xl bg-indigo-600 py-2.5 text-sm font-medium text-white hover:bg-indigo-700"
            >
              {t("console.partenaires.fermer")}
            </button>
          </div>
        ) : (
          <form
            data-testid="banque-formulaire"
            onSubmit={(event) => {
              event.preventDefault();
              if (!complete) return;
              void (async () => {
                setBusy(true);
                setRefusal(null);
                try {
                  await props.openBank({
                    name: name.trim(),
                    responsibleName,
                    responsibleEmail: email.trim(),
                    identityVerified,
                    conventionReceived: agreementReceived,
                  });
                  setSent(name.trim());
                  setEpoch((now) => now + 1);
                  props.onOpened();
                } catch (failure) {
                  setRefusal(
                    (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"),
                  );
                } finally {
                  setBusy(false);
                }
              })();
            }}
          >
            <p className="mb-4 text-xs text-gray-500">{t("console.partenaires.ajouterBanqueSous")}</p>
            <label className="mb-3 block text-xs font-medium text-gray-600">
              {t("console.partenaires.nomBanque")}
              <input
                data-testid="banque-nom"
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                }}
                placeholder="Ecobank Mali"
                className="mt-1.5 block w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              />
            </label>
            <label className="mb-1 block text-xs font-medium text-gray-600">
              {t("console.partenaires.responsableInvite")}
              <div className="mt-1.5 flex items-center gap-2.5 rounded-xl border border-gray-200 px-3 py-2.5">
                <Mail className="h-4 w-4 text-gray-500" aria-hidden="true" />
                <input
                  data-testid="banque-email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                  }}
                  placeholder="prenom.nom@banque.ml"
                  className="min-w-0 flex-1 text-sm outline-none"
                />
              </div>
            </label>
            <p data-testid="banque-responsable-nom" className="mb-3 text-xs text-gray-500">
              {responsibleName === "" ? t("console.partenaires.nomDeduit") : responsibleName}
            </p>
            <label className="mb-2 flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                data-testid="banque-identite"
                checked={identityVerified}
                onChange={(event) => {
                  setIdentityVerified(event.target.checked);
                }}
              />
              {t("console.concession.identiteVerifiee")}
            </label>
            <label className="mb-4 flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                data-testid="banque-convention"
                checked={agreementReceived}
                onChange={(event) => {
                  setAgreementReceived(event.target.checked);
                }}
              />
              {t("console.concession.conventionRecue")}
            </label>
            <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50 px-3.5 py-3">
              <p className="text-sm leading-relaxed text-gray-600">
                {t("console.partenaires.banqueModalNote")}
              </p>
              <p className="mt-1 text-sm leading-relaxed text-gray-500">
                {t("console.partenaires.contactEnMots")}
              </p>
            </div>
            {refusal === null ? null : (
              <p role="alert" data-testid="banque-refus" className="mb-3 text-sm text-rose-600">
                {refusal}
              </p>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={close}
                className="text-sm font-medium text-gray-600 hover:text-gray-700"
              >
                {t("console.equipe.annuler")}
              </button>
              <button
                type="submit"
                data-testid="banque-creer"
                disabled={!complete || busy}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-40"
              >
                {t("console.partenaires.creerEtInviter")}
              </button>
            </div>
          </form>
        )}
      </PartnerAddModal>
    </div>
  );
}

/**
 * `FX-3`, `M3` (`S2-4`): **a pending invitation's two gestures, and the revoke says what it costs.**
 *
 * The sitting met « Inviter le responsable » greyed with neither beside it, two banks at « Invitation
 * envoyée » with no users, and one invitation token exposed in a screenshot. `M1` gave the burn its door;
 * this is the hand that reaches it.
 *
 * **Each reports the DOOR's own sentence**, and a `null` answer is the success , the shape the fiche's
 * resend already has, so a send that failed cannot read as one that left (`P0-IDN-062`'s lesson, kept).
 * After either, the list is re-read: a revoked invitation leaves the seat open again, and the card must stop
 * offering the pair and start offering « Inviter le responsable ».
 */
function InvitationControls(props: {
  readonly invitationId: string;
  readonly organisationId: string;
  readonly resend?: (invitationId: string) => Promise<string | null>;
  readonly revoke?: (invitationId: string) => Promise<string | null>;
  readonly onDone: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const [word, setWord] = useState<string | null>(null);
  const act = (
    door: ((invitationId: string) => Promise<string | null>) | undefined,
    done: Parameters<typeof t>[0],
  ): void => {
    if (door === undefined) return;
    setBusy(true);
    setWord(null);
    void door(props.invitationId)
      .then((refusal) => {
        setWord(refusal ?? t(done));
        if (refusal === null) props.onDone();
      })
      .finally(() => {
        setBusy(false);
      });
  };
  return (
    <div className="flex flex-col items-end gap-1" data-testid={`banque-${props.organisationId}-invitation`}>
      <div className="flex items-center gap-2">
        {props.resend === undefined ? null : (
          <button
            type="button"
            disabled={busy}
            data-testid={`banque-${props.organisationId}-renvoyer`}
            onClick={() => {
              act(props.resend, "console.partenaires.invitationRenvoyee");
            }}
            className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t("console.partenaires.invitationRenvoyer")}
          </button>
        )}
        {props.revoke === undefined ? null : (
          <button
            type="button"
            disabled={busy}
            title={t("console.partenaires.invitationRevoquerAide")}
            data-testid={`banque-${props.organisationId}-revoquer`}
            onClick={() => {
              act(props.revoke, "console.partenaires.invitationRevoquee");
            }}
            className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            {t("console.partenaires.invitationRevoquer")}
          </button>
        )}
      </div>
      {word === null ? null : (
        <p
          role="status"
          data-testid={`banque-${props.organisationId}-invitation-mot`}
          className="max-w-xs text-right text-xs text-gray-500"
        >
          {word}
        </p>
      )}
    </div>
  );
}
